import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Loader2, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { pb } from '../../lib/pocketbaseClient';

// ক্যাটাগরি অপশন (আপনার ইচ্ছামতো পরিবর্তন করুন)
const CATEGORY_OPTIONS = [
  'Architecture',
  'Interior Design',
  'Construction',
  'Vastu Shastra'
];

const EBookForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [form, setForm] = useState({
    title: '',
    slug: '',
    author: '',
    description: '',
    category: '',
    sellingPrice: 0,
    mrp: 0,
    status: 'draft',
    featured: false,
    languages: [],
    featuredImage: null,
    pdfFile: null,
    totalDownloads: 0,
  });
  const [languagesInput, setLanguagesInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);

  // Edit মোডে ডাটা লোড
  useEffect(() => {
    if (isEditing) {
      const fetchEBook = async () => {
        try {
          const record = await pb.collection('ebooks').getOne(id);
          setForm({
            title: record.title || '',
            slug: record.slug || '',
            author: record.author || '',
            description: record.description || '',
            category: record.category || '',
            sellingPrice: record.sellingPrice || 0,
            mrp: record.mrp || 0,
            status: record.status || 'draft',
            featured: record.featured || false,
            languages: record.languages || [],
            featuredImage: null,
            pdfFile: null,
            totalDownloads: record.totalDownloads || 0,
          });
        } catch (error) {
          console.error(error);
          toast.error('Failed to load eBook');
          navigate('/ebooks');
        } finally {
          setFetching(false);
        }
      };
      fetchEBook();
    }
  }, [id, isEditing, navigate]);

  // হ্যান্ডেল চেঞ্জ (টেক্সট, নাম্বার, চেকবক্স, ফাইল)
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      if (name === 'featuredImage') {
        setForm({ ...form, featuredImage: files ? files[0] : null });
      } else if (name === 'pdfFile') {
        setForm({ ...form, pdfFile: files ? files[0] : null });
      }
    } else if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
    } else if (type === 'number') {
      setForm({ ...form, [name]: parseFloat(value) || 0 });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // সিলেক্ট ফিল্ডের জন্য আলাদা হ্যান্ডলার
  const handleSelectChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  // ল্যাঙ্গুয়েজ যোগ
  const addLanguage = () => {
    const trimmed = languagesInput.trim();
    if (trimmed && !form.languages.includes(trimmed)) {
      setForm({ ...form, languages: [...form.languages, trimmed] });
      setLanguagesInput('');
    }
  };

  // ল্যাঙ্গুয়েজ রিমুভ
  const removeLanguage = (lang) => {
    setForm({ ...form, languages: form.languages.filter(l => l !== lang) });
  };

  // সাবমিট
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.category) {
      toast.error('Please select a category');
      return;
    }
    setLoading(true);

    try {
      const data = new FormData();
      const fields = {
        title: form.title,
        slug: form.slug || form.title.toLowerCase().replace(/\s+/g, '-'),
        author: form.author || '',
        description: form.description || '',
        category: form.category,
        sellingPrice: form.sellingPrice || 0,
        mrp: form.mrp || 0,
        status: form.status,
        featured: form.featured,
        languages: JSON.stringify(form.languages),
        totalDownloads: form.totalDownloads || 0,
      };
      Object.entries(fields).forEach(([key, val]) => data.append(key, String(val)));

      if (form.featuredImage) data.append('featuredImage', form.featuredImage);
      if (form.pdfFile) data.append('pdfFile', form.pdfFile);

      if (isEditing) {
        await pb.collection('ebooks').update(id, data);
        toast.success('eBook updated successfully');
      } else {
        await pb.collection('ebooks').create(data);
        toast.success('eBook created successfully');
      }
      navigate('/ebooks');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save eBook');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        {/* হেডার */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-800">
            {isEditing ? 'Edit eBook' : 'Add New eBook'}
          </h2>
          <Button variant="outline" onClick={() => navigate('/ebooks')}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* প্রথম সারি */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Title *</Label>
              <Input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                className="mt-1.5"
                placeholder="e.g., Modern Architecture Guide"
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="Auto-generated"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Author</Label>
              <Input
                name="author"
                value={form.author}
                onChange={handleChange}
                placeholder="Author name"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Category *</Label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                required
                className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none bg-white"
              >
                <option value="">Select Category</option>
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Selling Price (₹)</Label>
              <Input
                type="number"
                name="sellingPrice"
                value={form.sellingPrice}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="mt-1.5"
                placeholder="0"
              />
            </div>
            <div>
              <Label>MRP (₹)</Label>
              <Input
                type="number"
                name="mrp"
                value={form.mrp}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="mt-1.5"
                placeholder="0"
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none bg-white"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
              </select>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                name="featured"
                checked={form.featured}
                onChange={handleChange}
                className="w-4 h-4 text-amber-500"
              />
              <Label>Featured</Label>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows="3"
              className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none"
              placeholder="Brief description of the eBook"
            />
          </div>

          {/* Languages */}
          <div>
            <Label>Languages</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={languagesInput}
                onChange={(e) => setLanguagesInput(e.target.value)}
                placeholder="e.g., English, Hindi"
                className="flex-1"
              />
              <Button type="button" onClick={addLanguage} variant="outline" className="shrink-0">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.languages.map((lang) => (
                <span
                  key={lang}
                  className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm flex items-center gap-1"
                >
                  {lang}
                  <button
                    type="button"
                    onClick={() => removeLanguage(lang)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* ফাইল আপলোড */}
          <div className="border-t pt-5 mt-5">
            <h3 className="font-semibold text-lg mb-3">Files</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Cover Image</Label>
                <input
                  type="file"
                  name="featuredImage"
                  accept="image/*"
                  onChange={handleChange}
                  className="mt-1.5 w-full text-sm"
                />
                {isEditing && form.featuredImage === null && (
                  <p className="text-sm text-slate-500 mt-1">Current cover image exists</p>
                )}
              </div>
              <div>
                <Label>PDF File</Label>
                <input
                  type="file"
                  name="pdfFile"
                  accept=".pdf"
                  onChange={handleChange}
                  className="mt-1.5 w-full text-sm"
                />
                {isEditing && form.pdfFile === null && (
                  <p className="text-sm text-slate-500 mt-1">Current PDF exists</p>
                )}
              </div>
            </div>
          </div>

          {/* বাটন */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-5">
            <Button type="button" variant="outline" onClick={() => navigate('/ebooks')}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? 'Update eBook' : 'Create eBook'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EBookForm;